import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@emotion/react';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _inherits from '@babel/runtime/helpers/esm/inherits';
import _possibleConstructorReturn from '@babel/runtime/helpers/esm/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/esm/getPrototypeOf';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { BodyText, CaptionText, HeadingText } from '@paypalcorp/pp-react-text';
import { Card } from '@paypalcorp/pp-react-card';
import { Icon } from '@paypalcorp/pp-react-icons';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import '../__generated__/styles/trackers.css';

/**
 *  method to validate is empty.
 * @param value
 * @returns {boolean}
 */
var isEmpty = function (value) {
  return value === null || _typeof(value) === 'object' && Object.keys(value).length === 0 || typeof value === 'string' && value.trim().length === 0;
};

/**
 *  Returns value from the content based on the key.
 * @param content
 * @param key
 * @returns {*}
 */
var getContentValue = function (content, key) {
  try {
    if (_typeof(content) === 'object' && !isEmpty(content)) {
      var path = key.split('.');
      while (path.length && (content = content[path.shift()]));
      return content;
    }
    return '';
  } catch (e) {
    return '';
  }
};

/**
 * Deep clone to remove the reference of the object.
 * @param data
 * @returns {*}
 */
var deepClone = function (data) {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (e) {
    return data;
  }
};

/**
 * Get corresponding directionality.
 * 1) First get the direction from html element where this component is loaded
 * 2) If no dir attribute value is found on html element then get the directionality from service response configs object.
 * 3) default fallback to ltr
 *
 * @param responseData
 * @returns {string}
 */
var getDirectionality = function (data) {
  try {
    var htmlEl = document.getElementsByTagName('html');
    var __htmlDir = htmlEl[0].dir;
    if (!isEmpty(__htmlDir)) {
      return __htmlDir;
    }
    var config = data && (data.info || data.config);
    if (config && config.directionality) {
      return config.directionality;
    }
    return 'ltr';
  } catch (e) {
    return 'ltr';
  }
};

// --------End of session storage ---------------

var getParsedErrorMessage = function (err) {
  if (!err) {
    return {};
  }
  return Object.assign({}, err, {
    // Explicitly pull Error's non-enumerable properties
    name: err.name,
    message: err.message,
    stack: err.stack
  });
};

/**
 * Finds the transaction from the given transactions using transaction id.
 *
 * @param transactions
 * @param transactionId
 * @returns {{index: (*|number|never), transaction: *}}
 */
var getTransaction = function (transactions, transactionId) {
  var index = transactions.findIndex(function (transaction) {
    return transaction.id === transactionId;
  });
  return {
    index: index,
    transaction: transactions[index]
  };
};
var setCollapseForTransactions = function (transactions) {
  var collapseAll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // If collapseAll is not set, expand the first transaction by default.
  if (Array.isArray(transactions) && transactions.length && !collapseAll) {
    transactions[0].isExpanded = true;
    return transactions;
  }

  // Else by default all transactions are set to false in service
  return transactions;
};
var getFinalTransactionsToDisplay = function (data) {
  var transactions = data.transactions,
    configs = data.configs;
  var finalTransactions = [];
  if (!Array.isArray(transactions)) {
    return finalTransactions;
  }
  var showEChecks = configs && configs.refunds && configs.refunds.showEChecks || false;

  // remote config is true to show all pending transactions including e-checks
  if (showEChecks) {
    finalTransactions = transactions;
  } else {
    // If remote config is false show only the transactions with lifecycles.
    finalTransactions = transactions.filter(function (transaction) {
      return !isEmpty(transaction.lifecycles);
    });
  }
  return finalTransactions;
};

/**
 *  Common method to set accordion expanded state.
 * @param transactions
 * @param selectedTransaction
 * @returns {*}
 */
var getUpdatedTransactionsToggle = function (transactions, selectedTransaction) {
  var nextStateTransactions = deepClone(transactions);
  var transactionToUpdate = getTransaction(transactions, selectedTransaction.id);
  nextStateTransactions[transactionToUpdate.index] = Object.assign({}, transactionToUpdate.transaction, {
    isExpanded: !transactionToUpdate.transaction.isExpanded
  });
  return nextStateTransactions;
};
var getToggleTypeForFPTI = function (transaction) {
  return !transaction.isExpanded ? 'expand' : 'collapse';
};

/**
 *  Filter states to get only the supported types for lifecycle.
 * @param states
 * @param config
 * @returns []
 */
var getSupportedStatesForRefunds = function (states, config) {
  if (Array.isArray(states) && states.length) {
    var supportedTypesInRc = config.refundsSupportedLifeCycleTypes || [];
    return states.filter(function (state) {
      return supportedTypesInRc.includes(state.name);
    });
  }
  return [];
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var defaultOptions = {
  pgrp: 'main:help:components',
  page: 'main:help:components',
  comp: 'helpComponents'
};
var analyticsObject = function () {
  try {
    return window && window.PAYPAL && window.PAYPAL.analytics;
  } catch (e) {
    return {};
  }
};

// Get FPTI otions
var getFPTIOptions = function (fptiOptions, eventType) {
  var fpti = window && window.fpti ? deepClone(window.fpti) : {};

  // clearing out error props in case they exist
  fpti.erpg = undefined;
  fpti.erfd = undefined;
  fpti.eccd = undefined;

  // Merge client passed fptiOptions with fpti object.
  fpti = _objectSpread$2(_objectSpread$2({}, fpti), fptiOptions);

  // TODO :: This logic needs to be revisited.
  // FPTI options passed through props

  if (isEmpty(fpti.page) || isEmpty(fpti.pgrp)) {
    fpti.page = defaultOptions.page;
    fpti.pgrp = defaultOptions.pgrp;
  }

  // Resetting to empty string for not to set link/pgln,phlk for ac events.
  fpti.link = '';
  fpti.pgln = '';
  fpti.pglk = '';

  // For click events
  if (fptiOptions.link) {
    fpti.link = fptiOptions.link;
    fpti.pgln = "".concat(fpti.page, "|").concat(fptiOptions.link);
    fpti.pglk = "".concat(fpti.pgrp, "|").concat(fptiOptions.link);
  }

  // Add event name to FPTI object
  fpti.event_name = '';
  if (fptiOptions.event_name) {
    fpti.event_name = fptiOptions.event_name;
  }

  // component name priority - fpti options props/analytics window object/default fallback
  if (isEmpty(fpti.comp)) {
    fpti.comp = defaultOptions.comp;
  }
  // Set event Type
  fpti.e = eventType ? eventType : 'ac'; // default activity event

  return fpti;
};

/**
 * @description
 *
 * Logs click metrics
 * @param {Object} fptiOptions
 * @property {String} options.data.page - Page name if not provided derived from the base page
 * @property {String} options.data.pgrp - Page group if not provided derived from the base page
 * e.g. var options = {data: {page:'page_name', pgrp: 'page_group_name'} }
 */
//https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/react.html#/links?_k=flh93g
var recordClickEvent = function (fptiOptions) {
  if (analyticsObject()) {
    var options = getFPTIOptions(fptiOptions, 'cl');
    window.PAYPAL.analytics.Analytics.prototype.recordClick({
      data: options
    });
  }
};

// Record Activity Events
var recordEvent = function (fptiOptions) {
  if (analyticsObject()) {
    var options = getFPTIOptions(fptiOptions, 'ac');
    window.PAYPAL.analytics.Analytics.prototype.recordEvent(options.event_name, {
      data: options
    });
  }
};

// FPTI Prop Type
var FPTI_OBJECT = {
  page: PropTypes.string.isRequired,
  pgrp: PropTypes.string.isRequired,
  comp: PropTypes.string
};

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
var ErrorBoundary = /*#__PURE__*/function (_React$Component) {
  _inherits(ErrorBoundary, _React$Component);
  var _super = _createSuper(ErrorBoundary);
  function ErrorBoundary(props) {
    var _this;
    _classCallCheck(this, ErrorBoundary);
    _this = _super.call(this, props);
    _this.state = {
      error: null,
      errorInfo: null
    };
    return _this;
  }
  _createClass(ErrorBoundary, [{
    key: "componentDidCatch",
    value: function componentDidCatch(err, errorInfo) {
      this.setState({
        err: err,
        errorInfo: errorInfo
      });
      if (typeof this.props.cbOnError === 'function') {
        // Callback function to track errors.
        this.props.cbOnError(err, errorInfo);
      }
    }
  }, {
    key: "render",
    value: function render() {
      if (!this.state.error) {
        // render any custom fallback UI
        return this.props.children;
      }
      if (this.props.suppressError) {
        return /*#__PURE__*/React.createElement("div", null);
      }
      return /*#__PURE__*/React.createElement("h1", null, 'Something went wrong in loading the widget.');
    }
  }]);
  return ErrorBoundary;
}(React.Component);
ErrorBoundary.propTypes = {
  suppressError: PropTypes.bool.isRequired,
  children: PropTypes.node,
  cbOnError: PropTypes.func.isRequired // callback function on error.
};

var progressLayoutTypes = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  AUTO: 'auto'
};
var statusTypes = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING'
};
var transactionStatusTypes = {
  E_CHECK: 'ECHECK_PENDING'
};
var supportedProgressLayoutTypes = [progressLayoutTypes.AUTO, progressLayoutTypes.HORIZONTAL, progressLayoutTypes.VERTICAL];

// //https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/onreadystatechange
var getData = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(url) {
    var config,
      method,
      req,
      response,
      _args = arguments;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          config = _args.length > 1 && _args[1] !== undefined ? _args[1] : {};
          method = config.method || 'GET';
          _context.prev = 2;
          req = new XMLHttpRequest();
          req.open(method, url, true);
          req.withCredentials = config.withCredentials || false;
          _context.next = 8;
          return new Promise(function (resolve, reject) {
            req.onreadystatechange = function () {
              if (req.readyState === 4) {
                if (req.status >= 200 && req.status < 300) {
                  var result = JSON.parse(req.response || req.responseText);
                  resolve(result);
                } else {
                  reject("HTTP Error: ".concat(req.status));
                }
              }
            };
            req.addEventListener('error', function (event) {
              reject(event);
            });
            req.send();
          });
        case 8:
          response = _context.sent;
          return _context.abrupt("return", {
            data: response
          });
        case 12:
          _context.prev = 12;
          _context.t0 = _context["catch"](2);
          return _context.abrupt("return", Promise.reject(_context.t0));
        case 15:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[2, 12]]);
  }));
  return function () {
    return _ref.apply(this, arguments);
  };
}();

var checkCircle = function () {
  var width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '24';
  var height = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '24';
  return /*#__PURE__*/React.createElement("svg", {
    style: {
      fill: 'white'
    },
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    width: width,
    height: height,
    viewBox: "0 0 48 48"
  }, /*#__PURE__*/React.createElement("title", null, "check-circle"), /*#__PURE__*/React.createElement("path", {
    d: "M18.259 31.519l18.011-19.203c0.378-0.403 1.011-0.423 1.413-0.045s0.423 1.011 0.045 1.413l-18.759 20c-0.402 0.428-1.084 0.42-1.475-0.018l-7.241-8.106c-0.368-0.412-0.332-1.044 0.079-1.412s1.044-0.332 1.412 0.079l6.514 7.291z"
  }));
};

// Steps component to render each step.
var Steps = function (props) {
  var steps = props.steps;
  var getStepClassName = function (step) {
    if (step.status === statusTypes.COMPLETED) {
      return 'progress-bar--container-body--line-container__step--completed';
    }
    if (step.status === statusTypes.PENDING) {
      return 'progress-bar--container-body--line-container__step--pending';
    }
    return '';
  };
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body--line-container__step"
  }, function renderSteps() {
    return steps.map(function (step, index) {
      return /*#__PURE__*/React.createElement("div", {
        className: getStepClassName(step),
        key: index
      }, checkCircle());
    });
  }()));
};
Steps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({}))
};

var StepLabels = function (props) {
  var steps = props.steps;
  return /*#__PURE__*/React.createElement(Fragment, null, function renderSteps() {
    return steps.map(function (step, index) {
      return /*#__PURE__*/React.createElement("div", {
        className: "progress-bar--container-body__steps-labels--label",
        key: index
      }, /*#__PURE__*/React.createElement(BodyText, {
        className: "no-margin"
      }, ' ', getContentValue(step, 'content.statusLabel'), ' '), /*#__PURE__*/React.createElement(BodyText, {
        className: "no-margin progress-bar--container-body__steps-labels--label_statusDateTime"
      }, getContentValue(step, 'content.statusDateTime'), ' '));
    });
  }());
};
StepLabels.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({}))
};

var StepsIndicator = function (props) {
  var steps = props.steps,
    completedProgressLineStyle = props.completedProgressLineStyle,
    progressLineStyle = props.progressLineStyle;
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body--line-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body--line-container__progress-line",
    style: progressLineStyle
  }, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body--line-container__progress-line--completed-progress",
    style: completedProgressLineStyle
  })), /*#__PURE__*/React.createElement(Steps, {
    steps: steps
  })), /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body__steps-labels"
  }, /*#__PURE__*/React.createElement(StepLabels, {
    steps: steps
  })));
};
StepsIndicator.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  completedProgressLineStyle: PropTypes.shape({}),
  progressLineStyle: PropTypes.shape({})
};
StepsIndicator.defaultProps = {};

// Horizontal progress bar
var ProgressBarHorizontal = function (props) {
  var steps = props.steps,
    layout = props.layout;
  var layoutClassName = "progress-bar--horizontal ".concat(layout === progressLayoutTypes.HORIZONTAL ? 'progress-bar--horizontal__forced' : '');
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: layoutClassName
  }, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body"
  }, /*#__PURE__*/React.createElement(StepsIndicator, {
    steps: steps,
    layout: layout,
    completedProgressLineStyle: function completedProgressWidthStyle() {
      var completedPercentage = 0;
      var stepWidth = 100 / (steps.length - 1);
      steps.forEach(function (step) {
        if (step.status === statusTypes.COMPLETED) {
          if (!step.start_state) {
            completedPercentage += stepWidth;
          }
        }
      });
      return {
        width: completedPercentage + '%',
        height: '100%'
      };
    }()
  }))));
};
ProgressBarHorizontal.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  layout: PropTypes.string
};
ProgressBarHorizontal.defaultProps = {};

// Vertical progress bar
var ProgressBarVertical = function (props) {
  var steps = props.steps,
    layout = props.layout;
  var layoutClassName = "progress-bar--vertical ".concat(layout === progressLayoutTypes.VERTICAL ? 'progress-bar--vertical__forced' : '');
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: layoutClassName
  }, /*#__PURE__*/React.createElement("div", {
    className: "progress-bar--container-body"
  }, /*#__PURE__*/React.createElement(StepsIndicator, {
    steps: steps,
    layout: layout,
    completedProgressLineStyle: function completedProgressHeight() {
      var completedPercentage = 0;
      var stepWidth = 100 / (steps.length - 1);
      steps.forEach(function (step) {
        if (step.status === statusTypes.COMPLETED) {
          if (!step.start_state) {
            completedPercentage += stepWidth;
          }
        }
      });
      return {
        height: completedPercentage + '%',
        width: '100%'
      };
    }(),
    progressLineStyle: function progressLineStyle() {
      return {
        height: steps.length * 80 + 'px'
      };
    }()
  }))));
};
ProgressBarVertical.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  layout: PropTypes.string
};
ProgressBarVertical.defaultProps = {};

// Progress Bar Version 1
var ProgressBarV1 = function (props) {
  var stepStates = props.stepStates,
    progressLayout = props.progressLayout,
    directionality = props.directionality;

  // Render corresponding progress bar layout based on the user props.

  return /*#__PURE__*/React.createElement("div", {
    className: "hc-trackers--progress-bar",
    dir: directionality
  }, function renderCorrespondingLayout() {
    // Validation to check if array and has min 1 state.
    if (!Array.isArray(stepStates) || Array.isArray(stepStates) && !stepStates.length) {
      return null;
    }
    switch (progressLayout) {
      case progressLayoutTypes.HORIZONTAL:
        return /*#__PURE__*/React.createElement(ProgressBarHorizontal, {
          steps: stepStates,
          layout: progressLayout
        });
      case progressLayoutTypes.VERTICAL:
        return /*#__PURE__*/React.createElement(ProgressBarVertical, {
          steps: stepStates,
          layout: progressLayout
        });
      default:
        return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(ProgressBarHorizontal, {
          steps: stepStates,
          layout: progressLayout
        }), /*#__PURE__*/React.createElement(ProgressBarVertical, {
          steps: stepStates,
          layout: progressLayout
        }));
    }
  }());
};
ProgressBarV1.propTypes = {
  stepStates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  progressLayout: PropTypes.oneOf([progressLayoutTypes.HORIZONTAL, progressLayoutTypes.VERTICAL, progressLayoutTypes.AUTO]),
  directionality: PropTypes.string
};
ProgressBarV1.defaultProps = {
  progressLayout: progressLayoutTypes.AUTO,
  directionality: 'ltr'
};

var TransactionItem = function (props) {
  var transaction = props.transaction,
    toggleTransactionHandler = props.toggleTransactionHandler,
    toggleKeyPressHandler = props.toggleKeyPressHandler,
    progressLayout = props.progressLayout,
    directionality = props.directionality,
    metaData = props.metaData,
    config = props.config;
  var transactionHeaderStyle = "transaction-header ".concat(transaction.isExpanded ? 'transaction-header--expanded' : '');

  //TODO:: if this TransactionItem component is able to reused by withdrawl component then move this below function to common helper.

  var showECheckContainer = function (item) {
    return /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container_echeck_content",
      dir: directionality
    }, /*#__PURE__*/React.createElement(BodyText, {
      className: "no-margin"
    }, getContentValue(item, 'content.eCheckStatus')));
  };
  var renderTransactionLifeCycle = function (item) {
    //Check if transaction has life cycles.
    if (!item || !Array.isArray(item.lifecycles) || item.lifecycles.length <= 0) {
      //If transaction has no life cycles and if it is E-CHECK refund.
      if (item.detailed_status === transactionStatusTypes.E_CHECK) {
        return showECheckContainer(item);
      }
      return null;
    }

    // If transaction has life cycle.
    // validate the lifecycle has states.
    var states = item.lifecycles[0].states;

    // TODO:: currently filtering the unsupported states in here -
    //  Revisit this during withdrawl or only other tracker implementation to make this a common place.
    var supportedStates = getSupportedStatesForRefunds(states, config);
    if (supportedStates && Array.isArray(supportedStates) && supportedStates.length) {
      return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "transaction-expanded--container_label",
        dir: directionality
      }, /*#__PURE__*/React.createElement(BodyText, null, getContentValue(metaData, 'transactionProgressHeader'))), /*#__PURE__*/React.createElement(ProgressBarV1, {
        stepStates: supportedStates,
        progressLayout: progressLayout,
        directionality: directionality
      }));
    }
    return /*#__PURE__*/React.createElement("div", null);
  };

  // Render transaction expanded content include transaction id, counter party info, and transaction amount

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("li", {
    key: transaction.id,
    className: "transaction-list--item"
  }, /*#__PURE__*/React.createElement("div", {
    className: transactionHeaderStyle,
    tabIndex: "0",
    onClick: function onClick() {
      return toggleTransactionHandler(transaction);
    },
    onKeyPress: function onKeyPress(e) {
      return toggleKeyPressHandler(e, transaction);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "transaction-header--left-section",
    dir: directionality
  }, /*#__PURE__*/React.createElement(BodyText, {
    className: "no-margin"
  }, getContentValue(transaction, 'content.transactionDateSH'))), /*#__PURE__*/React.createElement("div", {
    className: "transaction-header--middle-section",
    dir: directionality
  }, /*#__PURE__*/React.createElement(BodyText, {
    className: "no-margin transaction-header--middle-section__transaction_title"
  }, getContentValue(transaction, 'counterparty.name'))), /*#__PURE__*/React.createElement("div", {
    className: "transaction-header--right-section",
    dir: directionality
  }, /*#__PURE__*/React.createElement("span", {
    className: "transaction-header--right-section__amount"
  }, /*#__PURE__*/React.createElement(BodyText, {
    className: "no-margin"
  }, getContentValue(transaction, 'content.grossAmount'), ' ', getContentValue(transaction, 'gross.currency_code'))), /*#__PURE__*/React.createElement("span", {
    className: "transaction-header--right-section__icon"
  }, function getAccordionIcon(item) {
    if (!item.isExpanded) {
      return /*#__PURE__*/React.createElement(Icon, {
        name: "chevron-down",
        size: "md"
      });
    }
    return /*#__PURE__*/React.createElement(Icon, {
      name: "chevron-up",
      size: "md"
    });
  }(transaction)))), function renderTransactionDetails(item) {
    //If transaction is not expanded return empty div.
    if (!item.isExpanded) {
      return /*#__PURE__*/React.createElement("div", null);
    }

    //if transaction is expanded render transaction details.
    return /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container"
    }, renderTransactionLifeCycle(item), /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container_transaction-details"
    }, /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container_transaction-details--left_container",
      dir: directionality
    }, /*#__PURE__*/React.createElement(BodyText, {
      strong: true,
      className: "transaction-expanded--container_transaction-details--left_container--trans_label"
    }, getContentValue(metaData, 'transactionIdLabel')), /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--left_container--trans_id"
    }, item.id)), /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container_transaction-details--right_container",
      dir: directionality
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(BodyText, {
      strong: true,
      className: "no-margin no-padding"
    }, getContentValue(metaData, 'transactionSummaryHeader')), /*#__PURE__*/React.createElement(CaptionText, {
      className: "no-margin no-padding"
    }, getContentValue(item, 'content.transactionCounterPartyName')), /*#__PURE__*/React.createElement(CaptionText, {
      className: "no-margin no-padding"
    }, getContentValue(item, 'content.transactionCounterPartyEmail'))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--right_container--trans_details_title"
    }, getContentValue(metaData, 'transactionSummaryDetailsHeader')), /*#__PURE__*/React.createElement("div", {
      className: "border-btm transaction-expanded--container_transaction-details--right_container--trans_amount_details"
    }, /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--right_container--trans_amount_details_sub_left "
    }, getContentValue(metaData, 'transactionSummaryTotal')), /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--right_container--trans_amount_details_sub_right"
    }, getContentValue(item, 'content.grossAmount'), ' ', getContentValue(item, 'gross.currency_code'))), /*#__PURE__*/React.createElement("div", {
      className: "transaction-expanded--container_transaction-details--right_container--trans_amount_details"
    }, /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--right_container--trans_amount_details_sub_left"
    }, getContentValue(metaData, 'transactionTotalAmountLabel')), /*#__PURE__*/React.createElement(BodyText, {
      className: "transaction-expanded--container_transaction-details--right_container--trans_amount_details_sub_right"
    }, getContentValue(item, 'content.grossAmount'), ' ', getContentValue(item, 'gross.currency_code')))))));
  }(transaction)));
};
TransactionItem.propTypes = {
  transaction: PropTypes.shape({}).isRequired,
  toggleTransactionHandler: PropTypes.func,
  toggleKeyPressHandler: PropTypes.func,
  progressLayout: PropTypes.string,
  directionality: PropTypes.string,
  metaData: PropTypes.shape({}),
  config: PropTypes.shape({})
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var RefundsTracker = function (props) {
  var data = props.data,
    componentName = props.componentName,
    fptiOptions = props.fptiOptions,
    progressLayout = props.progressLayout,
    directionality = props.directionality,
    transactionsToDisplay = props.transactionsToDisplay,
    collapseAll = props.collapseAll,
    version = props.version;
  var _useState = useState(setCollapseForTransactions(transactionsToDisplay, collapseAll)),
    _useState2 = _slicedToArray(_useState, 2),
    transactions = _useState2[0],
    updateTransactions = _useState2[1];

  // On component mount, Fire impression event.
  useEffect(function () {
    //If has transactions then fire an impression event to state the component is loaded.
    if (Array.isArray(transactions) && transactions.length) {
      // Fire activity event
      recordEvent(_objectSpread$1({
        comp: componentName,
        event_name: 'hc_refund_tracker_widget_shown'
      }, fptiOptions));
    }
  }, []);

  // Toggle accordion handler - this is a callback method to TransactionItem
  var __toggleAccordionHandler = function (selectedTransaction) {
    var updatedTransactions = getUpdatedTransactionsToggle(transactions, selectedTransaction);
    updateTransactions(updatedTransactions);

    // FPTI
    var toggleType = getToggleTypeForFPTI(selectedTransaction);
    var updatedFptiOptions = _objectSpread$1({
      link: "refunds-tracker|toggle|".concat(selectedTransaction.id, "|").concat(toggleType),
      comp: componentName
    }, fptiOptions);
    recordClickEvent(updatedFptiOptions);
  };

  // transaction toggle on key press handler.
  var __toggleOnKeyPressHandler = function (e, transactionId) {
    if (e.which === 13) {
      __toggleAccordionHandler(transactionId);
    }
  };
  var renderTransactionList = function () {
    return /*#__PURE__*/React.createElement("ul", {
      className: "transaction-list"
    }, transactions.map(function (transaction) {
      return /*#__PURE__*/React.createElement(TransactionItem, {
        key: transaction.id,
        transaction: transaction,
        progressLayout: progressLayout,
        directionality: directionality,
        toggleTransactionHandler: __toggleAccordionHandler,
        toggleKeyPressHandler: __toggleOnKeyPressHandler,
        metaData: data.metaData,
        config: data.configs
      });
    }));
  };
  return /*#__PURE__*/React.createElement(Fragment, null, function renderRefundsTracker() {
    // If transactions to show then render the component else return null;
    if (Array.isArray(transactions) && transactions.length) {
      if (version === 'v2') {
        return /*#__PURE__*/React.createElement("div", {
          className: "hc-help-refunds-tracker",
          dir: directionality
        }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.Header, null, getContentValue(data.metaData, 'trackerHeader')), /*#__PURE__*/React.createElement("div", {
          className: "transaction-container",
          dir: directionality
        }, renderTransactionList())));
      }
      return /*#__PURE__*/React.createElement("div", {
        className: "hc-help-refunds-tracker",
        dir: directionality
      }, /*#__PURE__*/React.createElement(HeadingText, {
        size: "sm"
      }, getContentValue(data.metaData, 'trackerHeader')), /*#__PURE__*/React.createElement("div", {
        className: "transaction-container",
        dir: directionality
      }, renderTransactionList()));
    }
    return null;
  }());
};
RefundsTracker.propTypes = {
  data: PropTypes.shape({
    transactions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    metaData: PropTypes.shape({}).isRequired,
    configs: PropTypes.shape({}).isRequired
  }).isRequired,
  transactionsToDisplay: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  componentName: PropTypes.string,
  fptiOptions: PropTypes.shape(FPTI_OBJECT),
  progressLayout: PropTypes.oneOf(supportedProgressLayoutTypes),
  directionality: PropTypes.string,
  collapseAll: PropTypes.bool,
  version: PropTypes.string
};

var sizes = {
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg'
};
var spinnerTypes = {
  SHIMMER: 'shimmer',
  LOADER: 'loader'
};

function Loader(props) {
  var spinnerType = props.spinnerType,
    spinnerSize = props.spinnerSize,
    customLoaderStyle = props.customLoaderStyle;
  return function renderLoader() {
    // Shimmer loader
    var customStyle;
    if (spinnerType === spinnerTypes.SHIMMER) {
      customStyle = customLoaderStyle || {
        height: '12rem',
        width: '100%'
      };
      return /*#__PURE__*/React.createElement("div", {
        style: customStyle
      }, /*#__PURE__*/React.createElement(Card, {
        isLoading: true
      }));
    }

    // Spinning loader

    customStyle = customLoaderStyle || {
      display: 'flex',
      justifyContent: 'center'
    };
    return /*#__PURE__*/React.createElement("div", {
      style: customStyle
    }, /*#__PURE__*/React.createElement(LoadingSpinner, {
      screenReaderText: "loading",
      size: spinnerSize
    }));
  }();
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var getTransactionsUrl = function () {
  return '/smarthelp/api/hc-get-transactions?refunds=true';
};
var Refunds = function (props) {
  var collapseAll = props.collapseAll,
    spinnerSize = props.spinnerSize,
    componentName = props.componentName,
    fptiOptions = props.fptiOptions,
    suppressError = props.suppressError,
    progressLayout = props.progressLayout,
    devMode = props.devMode,
    devData = props.devData,
    version = props.version,
    spinnerType = props.spinnerType,
    customLoaderStyle = props.customLoaderStyle;

  // state
  var _useState = useState({
      originalData: null,
      isLoading: true,
      transactionsToDisplay: null,
      hasError: false,
      directionality: 'ltr'
    }),
    _useState2 = _slicedToArray(_useState, 2),
    refundsData = _useState2[0],
    updateRefundsData = _useState2[1];
  var getRefundTransactionsData = function () {
    getData(getTransactionsUrl()).then(function (response) {
      var data = response.data;
      return updateRefundsData({
        originalData: data,
        transactionsToDisplay: getFinalTransactionsToDisplay(data),
        isLoading: false,
        hasError: false,
        directionality: getDirectionality(data)
      });
    }).catch(function () {
      return updateRefundsData({
        isLoading: false,
        hasError: true,
        transactions: null,
        directionality: 'ltr'
      });
    });
  };

  // On component mount
  useEffect(function () {
    // On dev mode get the dummy data which is passed through props
    if (devMode) {
      return setTimeout(function () {
        return updateRefundsData({
          originalData: devData,
          transactionsToDisplay: getFinalTransactionsToDisplay(devData),
          isLoading: false,
          hasError: false,
          directionality: getDirectionality(devData)
        });
      }, 2000);
    }
    return getRefundTransactionsData();
  }, []);

  // Method to handle events on component error.
  // On component error fire impression event to track error cases.
  return /*#__PURE__*/React.createElement("div", {
    className: "hc-help-tracker"
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    suppressError: suppressError,
    cbOnError: function handleErrorCatch(err, errorInfo) {
      var errorFptiOptions = {
        page: "".concat(fptiOptions.page, "-error"),
        pgrp: fptiOptions.pgrp,
        event_name: 'hc_refund_tracker_widget_catch_error',
        error_description: getParsedErrorMessage(err),
        error_source: errorInfo
      };
      recordEvent(_objectSpread({
        comp: componentName
      }, errorFptiOptions));
    }
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: PAYPAL_THEME
  }, function renderRefundTracker() {
    var isLoading = refundsData.isLoading,
      hasError = refundsData.hasError,
      transactionsToDisplay = refundsData.transactionsToDisplay,
      originalData = refundsData.originalData,
      directionality = refundsData.directionality;

    // If data is not loaded yet. Return spinner/loader
    if (isLoading) {
      return /*#__PURE__*/React.createElement("div", {
        className: "hc-help-tracker--spinner"
      }, /*#__PURE__*/React.createElement(Loader, {
        spinnerType: spinnerType,
        spinnerSize: spinnerSize,
        customLoaderStyle: customLoaderStyle
      }));
    }

    // If has any error with XHR call. fire an FPTI.
    if (hasError) {
      recordEvent(_objectSpread({
        comp: componentName,
        event_name: 'hc_refund_tracker_widget_service_error'
      }, fptiOptions));
      return /*#__PURE__*/React.createElement("div", null);
    }

    // If transaction to display is less than the original data after the filter
    // then track it so that we can know why the transaction are missing.
    var transactions = originalData.transactions;
    if ((Array.isArray(transactions) && transactions.length) > (Array.isArray(transactionsToDisplay) && transactionsToDisplay.length)) {
      recordEvent(_objectSpread({
        comp: componentName,
        event_name: 'hc_refund_tracker_missing_transactions'
      }, fptiOptions));
    }

    // If no transactions exists after filter based on the remote configs and statuses
    if (!(Array.isArray(transactionsToDisplay) && transactionsToDisplay.length)) {
      recordEvent(_objectSpread({
        comp: componentName,
        event_name: 'hc_refund_tracker_widget_not_shown'
      }, fptiOptions));
      return /*#__PURE__*/React.createElement("div", null);
    }
    return /*#__PURE__*/React.createElement(RefundsTracker, {
      transactionsToDisplay: transactionsToDisplay,
      progressLayout: progressLayout,
      fptiOptions: fptiOptions,
      componentName: componentName,
      data: originalData,
      directionality: directionality,
      collapseAll: collapseAll,
      version: version
    });
  }())));
};

// Refunds Tracker props
Refunds.propTypes = {
  progressLayout: PropTypes.oneOf(supportedProgressLayoutTypes),
  suppressError: PropTypes.bool,
  fptiOptions: PropTypes.shape(FPTI_OBJECT).isRequired,
  spinnerSize: PropTypes.oneOf([sizes.SMALL, sizes.MEDIUM, sizes.LARGE]),
  componentName: PropTypes.string.isRequired,
  version: PropTypes.string,
  collapseAll: PropTypes.bool,
  spinnerType: PropTypes.oneOf([spinnerTypes.SHIMMER, spinnerTypes.LOADER]),
  customLoaderStyle: PropTypes.string,
  // development/github pages purpose only
  devMode: PropTypes.bool,
  devData: PropTypes.shape({})
};

// Refunds Tracker default props
Refunds.defaultProps = {
  progressLayout: progressLayoutTypes.AUTO,
  suppressError: true,
  spinnerSize: sizes.LARGE,
  version: 'v1',
  collapseAll: true,
  devMode: false,
  spinnerType: spinnerTypes.SHIMMER
};

export { Refunds as HcRefundsTracker };
